/**
 * **The Talia card** — one task at a glance, the look the owner picked on 23 September 2026
 * (`docs/design/karty-zadan-2026-09-24/canvas.js` `wide()`): the kind in the head, what explains it
 * under it, and the answers in the foot. Its CONTENT is the `/zadania` card's (owner, 24 September
 * 2026) — the same `TaskBody`, drawn at its `quick` or `short` density.
 *
 * ── No big figure (owner, 25 September 2026) ─────────────────────────────────────────────────
 * The canon's left column held one large number — "22/24 serii", "3 odpowiedzi" — and on these two
 * small cards it was a visual trick that took a third of the width from the data that explains it.
 * The body takes the whole width now; where the number IS the news ("za 7 dni", "9 dni bez
 * treningu") it is folded into the head's one muted meta line (`model/taskContent`'s `glance`). The
 * `/zadania` desk keeps its figures.
 *
 * Two variants, one component:
 *
 * - `quick` — the client's Przegląd, where the page already is the person: the head is the kind of
 *   task, its meta line and its age, and the card holds ONE height for every kind (`--h-talia-card`)
 *   so switching tabs never moves the table under it. Nothing inside it scrolls: every block's
 *   `quick` density is laid out to fit that height.
 * - `short` — the Pulpit's "Szybki feedback": the head leads with WHO (the avatar, the full name,
 *   the kind under it, the hour on the right) at main's feedback-row sizes, in the same place on
 *   every kind, on the lighter surface of the bento's rows, and as tall as its content. A session
 *   and a clip draw their visual — the ring, a small poster — on the body's left, with the meta line
 *   opening the column beside it, so the head carries the kind alone (owner, 26 September 2026).
 *
 * The foot is the host's: each surface answers the queue its own way (the Pulpit's closing gesture
 * and focus hand-off, the Przegląd's own responder), so this card only places what it is given.
 */

import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { AvatarChip } from '~/shared/ui/AvatarChip';

import type { TaskSlots } from '../lib/taskWrites';
import { useTaskContent } from '../lib/useTaskContent';
import type { AttentionItem } from '../model/attentionItems';
import { ReasonTile } from './ReasonTile';
import { bodyCarriesGlance, TaskBody } from './TaskBody';
import styles from './taliaCard.module.css';

interface TaskTaliaCardProps {
  readonly item: AttentionItem;
  readonly variant: 'quick' | 'short';
  readonly now: Date;
  /** How long it has waited, worded by the host ("14 godz.", "3 dni"). */
  readonly age: string;
  readonly slots: TaskSlots;
  readonly reducedMotion: boolean;
  /** The foot: the primary and the two responses, as the surface wires them. */
  readonly foot: ReactNode;
  /** What opens under the card — the Pulpit's note composer. */
  readonly after?: ReactNode;
  /** The tab panel's ids, where the card stands under `TaskTaliaTabs`. */
  readonly panel?: { readonly id: string; readonly labelledBy: string };
  /**
   * Where a host puts the keyboard when the card it held left and this one took its place — the
   * Przegląd's hand-off. Given, the kind's line becomes a programmatic focus target.
   */
  readonly headingRef?: Ref<HTMLSpanElement>;
}

export function TaskTaliaCard({
  item,
  variant,
  now,
  age,
  slots,
  reducedMotion,
  foot,
  after,
  panel,
  headingRef,
}: TaskTaliaCardProps) {
  const { t } = useTranslation();
  const content = useTaskContent(item, now);
  const kindLine =
    content.glance === '' || bodyCarriesGlance(item, variant) ? t(item.titleKey) : `${t(item.titleKey)} · ${content.glance}`;

  return (
    <article
      className={`${String(styles.card)} ${String(styles[variant])}`}
      data-reason={item.reason}
      {...(panel === undefined ? {} : { id: panel.id, role: 'tabpanel', 'aria-labelledby': panel.labelledBy })}
      aria-label={panel === undefined ? t(item.titleKey) : undefined}
    >
      <header className={styles.head}>
        {variant === 'short' ? (
          <>
            <AvatarChip className={styles.avatar} initials={item.initials} avatarUrl={item.avatarUrl} />
            <span className={styles.who}>
              <b className={styles.name}>{item.fullName}</b>
              <span className={styles.kind}>{kindLine}</span>
            </span>
          </>
        ) : (
          <>
            <ReasonTile item={item} size="md" decorative />
            <span ref={headingRef} className={styles.kind} tabIndex={headingRef === undefined ? undefined : -1}>
              {t(item.titleKey)}
            </span>
            {content.glance === '' ? null : <span className={styles.glance}>{content.glance}</span>}
          </>
        )}
        <span className={styles.age}>{age}</span>
      </header>

      <div className={styles.body}>
        <TaskBody
          item={item}
          density={variant}
          content={content}
          now={now}
          slots={slots}
          reducedMotion={reducedMotion}
        />
      </div>

      <footer className={styles.foot}>{foot}</footer>
      {after === undefined ? null : <div className={styles.after}>{after}</div>}
    </article>
  );
}
